import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase, isAdminEmail } from '@/lib/supabase';
import { Session, User as SupabaseUser } from '@supabase/supabase-js';
import { queryClient } from '@/lib/queryClient';

interface User {
  id: string;
  email: string;
  role: 'admin' | 'read-only';
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  loginWithMagicLink: (email: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => Promise<void>;
  isAdmin: () => boolean;
  isAuthenticated: () => boolean;
  getAccessToken: () => string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

function mapSupabaseUser(supabaseUser: SupabaseUser | null): User | null {
  if (!supabaseUser) return null;

  return {
    id: supabaseUser.id,
    email: supabaseUser.email || '',
    role: isAdminEmail(supabaseUser.email) ? 'admin' : 'read-only'
  };
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Check for recovery token in URL (handles redirect from Supabase)
    const url = new URL(window.location.href);
    const isRecovery = url.searchParams.get('type') === 'recovery' ||
                       url.hash.includes('type=recovery');

    if (isRecovery && window.location.pathname !== '/reset-password') {
      // Redirect to reset password page with the current URL params
      window.location.href = '/reset-password' + window.location.search + window.location.hash;
      return;
    }

    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(mapSupabaseUser(session?.user ?? null));
      setIsLoading(false);
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('Auth event:', event);
        setSession(session);
        setUser(mapSupabaseUser(session?.user ?? null));
        setIsLoading(false);

        // Handle password recovery - redirect to reset password page
        if (event === 'PASSWORD_RECOVERY') {
          window.location.href = '/reset-password';
        }
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  const login = async (email: string, password: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (error) {
        return { success: false, error: error.message };
      }

      if (!isAdminEmail(data.user?.email)) {
        // Sign out non-admin users
        await supabase.auth.signOut();
        return { success: false, error: 'Access denied. Admin privileges required.' };
      }

      return { success: true };
    } catch (error) {
      console.error('Login failed:', error);
      return { success: false, error: 'Login failed. Please try again.' };
    }
  };

  const loginWithMagicLink = async (email: string): Promise<{ success: boolean; error?: string }> => {
    try {
      if (!isAdminEmail(email)) {
        return { success: false, error: 'Access denied. Admin privileges required.' };
      }

      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          emailRedirectTo: `${window.location.origin}/admin`
        }
      });

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (error) {
      console.error('Magic link failed:', error);
      return { success: false, error: 'Failed to send magic link. Please try again.' };
    }
  };

  const logout = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
    // Clear all cached data to prevent stale data after logout
    queryClient.clear();
  };

  const isAdmin = () => user?.role === 'admin';
  const isAuthenticated = () => !!user && !!session;
  const getAccessToken = () => session?.access_token ?? null;

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        isLoading,
        login,
        loginWithMagicLink,
        logout,
        isAdmin,
        isAuthenticated,
        getAccessToken
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
